<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useTransactionStatus } from "../composables/useTransactionStatus";
import { clearBuyDraft } from "../buyDraft";
import { EVENT_INFO_URL } from "../siteLinks";

const route = useRoute();

const transactionId = computed<string>(() => {
  const v = route.query.txn;
  return typeof v === "string" ? v : "";
});

const token = computed<string>(() => {
  const v = route.query.token;
  return typeof v === "string" ? v : "";
});

const { status, error, errorCode, timedOut, start } = useTransactionStatus();

const completed = computed(() => status.value?.status === "completed");
const failed = computed(() => status.value?.status === "failed");
const abandoned = computed(() => status.value?.status === "abandoned");
const tickets = computed(() => status.value?.tickets ?? []);

// The /return page is reachable via SumUp's manual "Back to merchant" link,
// which fires regardless of payment outcome. Keep the H1 neutral until we
// know the status — only congratulate on a confirmed completion.
const titleKey = computed(() => {
  if (completed.value && tickets.value.length > 0) return "buy.return.titleCompleted";
  if (failed.value) return "buy.return.titleFailed";
  if (abandoned.value) return "buy.return.titleAbandoned";
  return "buy.return.titlePending";
});

// Back-to-buy link: only render when we know which event the customer was
// buying for. Shown from terminal states (completed/failed/abandoned) and —
// paired with the check-your-inbox hint — from timed-out, where SumUp's
// manual "back to merchant" link would otherwise strand the customer.
// Omitted only while actively pending (the page still auto-resolves).
// Preserve `mode=test` so a test-mode round-trip doesn't silently drop the
// customer back onto the production buy page.
const backHref = computed<string | null>(() => {
  const s = status.value;
  if (!s?.eventId) return null;
  const base = `/event/${encodeURIComponent(s.eventId)}`;
  return s.isTest ? `${base}?mode=test` : base;
});

onMounted(() => {
  if (!transactionId.value || !token.value) return;
  start(transactionId.value, token.value);
});

// On a confirmed completion, drop the buy-form snapshot so a fresh visit to
// the buy page doesn't pre-fill the cart the customer just paid for. The
// snapshot is intentionally kept on failed/abandoned/timed-out — that's the
// whole point of the back-to-buy link.
watch(completed, (isDone) => {
  if (isDone) clearBuyDraft();
});
</script>

<template>
  <div class="mx-auto max-w-xl px-4 py-6">
    <h1 class="text-2xl font-semibold text-slate-900">{{ $t(titleKey) }}</h1>

    <div
      v-if="!transactionId || !token"
      class="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-red-800"
    >
      {{ $t("buy.errors.missingReturnParams") }}
    </div>

    <template v-else>
      <p class="mt-3 text-xs text-slate-500">
        {{ $t("buy.return.transactionId") }}: <span class="font-mono">{{ transactionId }}</span>
      </p>

      <template v-if="completed && tickets.length > 0">
        <div
          class="mt-5 rounded-md border border-emerald-300 bg-emerald-50 p-4"
          data-testid="return-completed"
        >
          <h2 class="text-lg font-semibold text-emerald-900">
            {{ $t("buy.return.completedHeader") }}
          </h2>
          <p class="mt-2 text-sm text-emerald-900">
            {{ $t("buy.return.completedHelp", { email: status?.customerEmail ?? "" }) }}
          </p>
          <ul class="mt-3 flex flex-col gap-2">
            <li v-for="(t, i) in tickets" :key="t.ticketId">
              <a
                :href="t.pdfUrl"
                class="inline-flex w-full items-center justify-center rounded-md border border-emerald-700 bg-white px-4 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-100"
                target="_blank"
                rel="noopener"
              >
                {{
                  t.category
                    ? $t("buy.return.downloadTicketLabeled", {
                        n: i + 1,
                        category: $t(`buy.return.category.${t.category}`),
                      })
                    : $t("buy.return.downloadTicket", { n: i + 1 })
                }}
              </a>
            </li>
          </ul>
          <p class="mt-3 text-xs text-emerald-800">{{ $t("buy.return.checkInbox") }}</p>
        </div>

        <!-- Navigation lives outside the ticket box, styled like the buy
             page's primary "Jetzt bezahlen" button. -->
        <div class="mt-5 flex flex-col gap-2">
          <a
            :href="EVENT_INFO_URL"
            class="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            rel="noopener"
            data-testid="return-back-to-event"
          >
            {{ $t("buy.return.backToEvent") }}
          </a>
          <a
            v-if="backHref"
            :href="backHref"
            class="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            target="_top"
            rel="noopener"
            data-testid="return-back-to-buy"
          >
            {{ $t("buy.return.backToBuy") }}
          </a>
        </div>
      </template>

      <div
        v-else-if="failed"
        class="mt-5 rounded-md border border-red-300 bg-red-50 p-4"
        data-testid="return-failed"
      >
        <p class="text-sm text-red-900">{{ $t("buy.errors.transactionFailed") }}</p>
        <a
          v-if="backHref"
          :href="backHref"
          class="mt-3 inline-flex items-center justify-center rounded-md border border-red-700 bg-white px-4 py-2 text-sm font-semibold text-red-900 hover:bg-red-100"
          target="_top"
          rel="noopener"
          data-testid="return-back-to-buy"
        >
          {{ $t("buy.return.backToBuy") }}
        </a>
      </div>

      <div
        v-else-if="abandoned"
        class="mt-5 rounded-md border border-amber-300 bg-amber-50 p-4"
        data-testid="return-abandoned"
      >
        <p class="text-sm text-amber-900">{{ $t("buy.errors.transactionAbandoned") }}</p>
        <a
          v-if="backHref"
          :href="backHref"
          class="mt-3 inline-flex items-center justify-center rounded-md border border-amber-700 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
          target="_top"
          rel="noopener"
          data-testid="return-back-to-buy"
        >
          {{ $t("buy.return.backToBuy") }}
        </a>
      </div>

      <template v-else-if="timedOut">
        <div
          class="mt-5 rounded-md border border-amber-300 bg-amber-50 p-4"
          data-testid="return-timedout"
        >
          <p class="text-sm text-amber-900">{{ $t("buy.return.timedOut") }}</p>
          <p class="mt-2 text-xs text-amber-800">{{ $t("buy.return.timedOutRebuyHint") }}</p>
        </div>

        <!-- SumUp's manual "back to merchant" link lands here with the payment
             often never attempted — without navigation the customer is stuck.
             Same buttons as the completed state; the hint above covers the
             rare case where the payment settles after all. -->
        <div class="mt-5 flex flex-col gap-2">
          <a
            :href="EVENT_INFO_URL"
            class="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            rel="noopener"
            data-testid="return-back-to-event"
          >
            {{ $t("buy.return.backToEvent") }}
          </a>
          <a
            v-if="backHref"
            :href="backHref"
            class="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
            target="_top"
            rel="noopener"
            data-testid="return-back-to-buy"
          >
            {{ $t("buy.return.backToBuy") }}
          </a>
        </div>
      </template>

      <div v-else class="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4">
        <p class="text-sm text-slate-700">{{ $t("buy.return.stillPending") }}</p>
      </div>

      <p
        v-if="error && !completed && !failed && !abandoned"
        class="mt-3 text-xs text-slate-500"
      >
        <span v-if="errorCode">[{{ errorCode }}]</span>
        {{ $t("buy.errors.statusFailed") }}
      </p>
    </template>
  </div>
</template>
